/**
 * Kiosk overlay for building a custom salad. Multi-select topping chips
 * in stacked categories inside a single scroll region; live selection
 * count in the title bar and on the Add button. Replaces the old
 * full-screen 'salad' step. Cancel/backdrop/× discard — nothing reaches
 * the cart until Add.
 */

import { useState } from 'react';
import { KioskOverlay } from './KioskOverlay';
import type { ToppingCategory } from '../../hooks/useMenu';

interface SaladBuilderOverlayProps {
  categories: ToppingCategory[];
  initialSelected?: number[];
  confirmLabel?: string; // "Add Salad to Order" | "Update Order"
  onAdd: (toppingIds: number[], toppingNames: string[]) => void;
  onCancel: () => void;
}

export function SaladBuilderOverlay({
  categories,
  initialSelected,
  confirmLabel = 'Add Salad to Order',
  onAdd,
  onCancel,
}: SaladBuilderOverlayProps) {
  const [selected, setSelected] = useState<number[]>(initialSelected ?? []);

  const toggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const commit = () => {
    const names = categories
      .flatMap((c) => c.toppings)
      .filter((t) => selected.includes(t.id))
      .map((t) => t.name);
    onAdd(selected, names);
  };

  return (
    <KioskOverlay
      title="Build Your Salad"
      headerBadge={`${selected.length} selected`}
      onDismiss={onCancel}
      maxWidth="max-w-5xl"
      footer={
        <div className="flex gap-4">
          <button
            onClick={commit}
            className="flex-1 h-20 bg-[#10348e] text-white rounded-2xl text-2xl font-semibold
                       hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all"
          >
            {selected.length > 0 ? `${confirmLabel} — ${selected.length} topping${selected.length !== 1 ? 's' : ''}` : confirmLabel}
          </button>
          <button
            onClick={onCancel}
            className="h-20 px-10 bg-gray-100 hover:bg-gray-200 active:bg-gray-300
                       text-gray-600 text-xl font-medium rounded-2xl transition-all"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-8">
        {categories.map((category) => (
          <div key={category.name}>
            <h3 className="text-xl font-medium text-gray-500 mb-4">{category.name}</h3>
            <div className="flex flex-wrap gap-3">
              {category.toppings.map((topping) => {
                const isSelected = selected.includes(topping.id);
                return (
                  <button
                    key={topping.id}
                    onClick={() => toggle(topping.id)}
                    className={`min-h-[72px] px-6 rounded-2xl text-xl font-medium transition-all
                               active:scale-[0.98] flex items-center gap-2
                               ${isSelected
                                 ? 'bg-[#10348e] text-white shadow-md'
                                 : 'bg-white text-gray-900 border-2 border-[#1e62ac] hover:bg-blue-50'}`}
                  >
                    {isSelected && <span className="text-xl">✓</span>}
                    <span>{topping.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </KioskOverlay>
  );
}
